import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import {
  LuArrowRight,
  LuBuilding2,
  LuCpu,
  LuHeartPulse,
  LuLandmark,
  LuZap,
} from 'react-icons/lu';

import RoleLinkCard from './RoleLinkCard';
import RoleLinkCardGrid from './RoleLinkCardGrid';
import SbrIcon from './SbrIcon';
import type { RoleLinkCardItem } from './RoleLinkCard';

export type IndustryBandItem = RoleLinkCardItem;

const defaultIndustries: IndustryBandItem[] = [
  { title: 'Energy & Utilities', Icon: LuZap },
  { title: 'Manufacturing & Industrial', Icon: LuBuilding2 },
  { title: 'Information Technology & Software', Icon: LuCpu },
  { title: 'Healthcare & Life Sciences', Icon: LuHeartPulse },
  { title: 'Financial Services', Icon: LuLandmark },
];

type IndustriesWeServeBandProps = {
  items?: IndustryBandItem[];
  title?: string;
  subtitle?: string;
  showFooter?: boolean;
  /** Dark navy band — use on Roles We Fill; Home uses light (default). */
  variant?: 'light' | 'dark';
  /** Grid matches Home; scroll is a horizontal row (default). */
  layout?: 'scroll' | 'grid';
  /** Tile shape passed to {@link RoleLinkCardGrid}. */
  cardLayout?: 'horizontal' | 'stacked';
  /** Desktop column count when stacked (Home industries = 5). */
  columns?: number;
};

export default function IndustriesWeServeBand({
  items = defaultIndustries,
  title = 'Industries We Serve',
  subtitle = 'Deep expertise across industries. Proven results everywhere.',
  showFooter = true,
  variant = 'light',
  layout = 'scroll',
  cardLayout = 'horizontal',
  columns,
}: IndustriesWeServeBandProps) {
  const variantClass =
    variant === 'dark' ? 'industries-band--dark bg-sbr-navy text-white' : 'industries-band--light bg-white';
  const useScrollOnDesktop = layout === 'scroll';
  const cardVariant = variant === 'dark' ? 'dark' : 'light';
  const industriesLink = '/industries';

  const gridTiles = (
    <RoleLinkCardGrid
      items={items}
      to={industriesLink}
      variant={cardVariant}
      layout={cardLayout}
      columns={columns ?? (cardLayout === 'stacked' ? items.length : undefined)}
      className={useScrollOnDesktop ? 'd-lg-none' : ''}
    />
  );

  return (
    <section
      className={`industries-band ${variantClass} py-5 py-lg-6`}
      aria-labelledby="industries-band-title"
    >
      <Container>
        <div className="industries-band__header text-center">
          <h2 id="industries-band-title" className="sbr-section__title fw-bold mb-2">
            {title}
          </h2>
          {subtitle ? (
            <p className="industries-band__subtitle mb-0 mx-auto">{subtitle}</p>
          ) : null}
        </div>

        {useScrollOnDesktop ? (
          <>
            {gridTiles}
            <div className="industries-band__row-scroll d-none d-lg-block">
              <div className="industries-band__row" role="list">
                {items.map((ind) => (
                  <RoleLinkCard
                    key={ind.title}
                    item={ind}
                    to={industriesLink}
                    variant={cardVariant}
                    className="industries-band__scroll-card"
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          gridTiles
        )}

        {showFooter ? (
          <div className="industries-band__footer">
            <Link className="industries-band__cta" to={industriesLink}>
              View All Industries
              <SbrIcon icon={LuArrowRight} size={18} className="ms-1" aria-hidden />
            </Link>
          </div>
        ) : null}
      </Container>
    </section>
  );
}
